import {
  BrowserMultiFormatReader,
  DecodeHintType,
  Result,
} from '@zxing/library';
import { useEffect, useMemo, useRef } from 'react';

export enum FacingMode {
  User = 'user',
  Environment = 'environment',
}

interface ZxingOptions {
  hints?: Map<DecodeHintType, any>;
  constraints?: MediaStreamConstraints;
  timeBetweenDecodingAttempts?: number;
  onResult?: (result: Result) => void;
  onError?: (error: Error) => void;
}

function createDefaultConstraints({ facingMode }: { facingMode: FacingMode }) {
  return {
    audio: false,
    video: {
      facingMode,
    },
  };
}

const useZxing = ({
  constraints = createDefaultConstraints({
    facingMode: FacingMode.Environment,
  }),
  hints,
  timeBetweenDecodingAttempts = 300,
  onResult = () => {},
  onError = () => {},
}: ZxingOptions = {}) => {
  const ref = useRef<HTMLVideoElement>(null);

  const reader = useMemo<BrowserMultiFormatReader>(() => {
    const instance = new BrowserMultiFormatReader(hints);
    instance.timeBetweenDecodingAttempts = timeBetweenDecodingAttempts;
    return instance;
  }, [hints, timeBetweenDecodingAttempts]);

  useEffect(() => {
    if (!ref.current) return;

    reader.decodeFromConstraints(constraints, ref.current, (result, error) => {
      if (result) onResult(result);
      if (error) onError(error);
    });
    return () => {
      reader.reset();
    };

    /* @ts-ignore */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ref, reader, constraints.video.facingMode]);

  return { ref };
};

interface BarcodeScannerProps {
  onResult: (result: Result) => void;
  onError?: (error: Error) => void;
  facingMode?: FacingMode;
}

export const BarcodeScanner: React.FC<BarcodeScannerProps> = ({
  facingMode = FacingMode.Environment,
  ...rest
}) => {
  const constraints = createDefaultConstraints({ facingMode });
  const { ref } = useZxing({ constraints, ...rest });

  return <video ref={ref} />;
};
